import { useContext, useState, useMemo, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { BoxFull, Button } from "iuvity-ui-components";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../context/index";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { InputText } from "iuvity-ui-components/dist/inputtext";
import { TableHeader } from "../datatable/TableHeader";
import { Actions } from "../datatable/Actions";
import { Column } from "primereact/column";
import { FormSkeleton } from "../skeleton/FormSkeleton";
import { FilterMatchMode } from "primereact/api";
import { CustomDialog, DialogProps } from "../dialog/CustomDialog";
import { formatStringToDate2 } from "../../helpers/formatDate";
import { EmptyTemplate } from "../datatable/EmptyTemplate";
import noResultsImage from "../../assets/Illustration_NoResults.svg";
import { Tag } from "iuvity-ui-components/dist/tag";
import { ActionType } from "../../hooks/useRoles";
import { useMasterCategory } from "../../hooks/useMasterCategory";
import { ImasterCategoryResponse } from "../../interfaces/roles.interface";
import { roleTypeTranslator } from "../../helpers/roleTypeTranslations";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";

interface RolesAdminProps {
  data: any;
  rolesAction: (action: ActionType, data: any) => Promise<string>;
  reloadFn: () => void;
  isDeleteComponent?: boolean;
}

const formBlankValues = {
  type: "",
  name: "",
  status: "",
  createdAt: ""
};

export const RolesAdminComponent: React.FC<RolesAdminProps> = ({
  isDeleteComponent = false,
  data,
  rolesAction,
  reloadFn,
}) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { language, pageFilter, selectedApp, isMainTenant, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const roleTypeId = selectedApp?.tenantid === (process.env['REACT_APP_CORPORATIVE_TENANT_ID'] || '')
    ? 'base' : 'client';

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: !!pageFilter
      ? pageFilter
      : formBlankValues,
  });

  // // ########## ROLTYPES TRANSLATIONS BLOCK ##########
  const { data: masterCategoriesList }: { data: ImasterCategoryResponse[] } = useMasterCategory("DATA");
  const roleTypes:ImasterCategoryResponse[] = masterCategoriesList?.filter((category:ImasterCategoryResponse) => (
      category.status === 'ACTIVE' &&
      category.categoryId === 'ROLE_TYPES'));
  const roleTypesObject = {
      base: roleTypes?.find((category: ImasterCategoryResponse) => category.id.toLocaleLowerCase() === 'base'),
      client: roleTypes?.find((category: ImasterCategoryResponse) => (
          category.id.toLocaleLowerCase() === 'client' ||
          category.id.toLocaleLowerCase() === 'cliente'
      )),
  }
  // // ########## ROLTYPES TRANSLATIONS BLOCK ##########

  const { control, setValue, getValues } = form;

  const columns = useMemo(() => {
    if (language) {
      let columList = [
        {
          field: "type",
          header: t("Roles.typeColumnTitle"),
        },
        {
          field: "name",
          header: t("Roles.nameColumnTitle").split(' ')[0],
        },
        {
          field: "roleCategory",
          header: t("Roles.roleCategoryColumnTitle"),
        },
        {
          field: "description",
          header: t("Roles.descriptionColumnTitle"),
        },
        {
          field: "status",
          header: t("Roles.statusColumnTitle"),
        },
        {
          field: "createdAt",
          header: t("Roles.createdAtColumnTitle"),
        },
        {
          field: "deletedAt",
          header: t("Roles.deletedAtColumnTitle"),
        },
        {
          field: "actions",
          header: t("Roles.actionsColumnTitle"),
        },
      ];
      return isDeleteComponent
        ? columList.filter((obj) => obj.field !== 'status' && obj.field !== 'createdAt')
        : columList.filter((obj) => obj.field !== 'deletedAt')
    } else {
      return [];
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, language]);

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: pageFilter?.type, matchMode: FilterMatchMode.CONTAINS },
    name: { value: pageFilter?.name, matchMode: FilterMatchMode.CONTAINS },
    roleCategory: { value: pageFilter?.roleCategory, matchMode: FilterMatchMode.CONTAINS },
    description: { value: pageFilter?.description, matchMode: FilterMatchMode.CONTAINS },
    status: { value: pageFilter?.status, matchMode: FilterMatchMode.CONTAINS },
    createdAt: { value: pageFilter?.createdAt, matchMode: FilterMatchMode.CONTAINS },
    deletedAt: { value: pageFilter?.deletedAt, matchMode: FilterMatchMode.CONTAINS },
  }
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [administrationFilters, setAdministrationFilters] = useState(defaultFilterValues);
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const onAction = (type: "CLONE" | "UPDATE" | "DETAIL", data?: any) => {
    if (type === "UPDATE" && data) {
      setAppState((prev) => ({
        ...prev,
        selectedRolesObject: data,
      }));
      navigate("/adminroles/editroles");
    }
    if (type === "CLONE" && data) {
      setAppState((prev) => ({
        ...prev,
        selectedRolesObject: data,
      }));
      navigate("/adminroles/cloneroles");
    }
    if (type === "DETAIL" && data) {
      setAppState((prev) => ({
        ...prev,
        selectedRolesObject: data,
      }));
      navigate("/adminroles/detailroles");
    }
  };

  const onDeleteAction = async (data: any) => {
    const actionResult = await rolesAction("DELETE", {id: data?.id, name: data?.name});
    if (actionResult === "SUCCESS") {
      reloadFn();
      setAdministrationFilters(defaultFilterValues);
    }
  };

  const onReactiveAction = async (data: any) => {
    const actionResult = await rolesAction("REACTIVE", {id: data?.id, name: data?.name});
    if (actionResult === "SUCCESS") {
      reloadFn();
      setAdministrationFilters(defaultFilterValues);
    }
  };

  const handleModalProps = async (type: 'DELETE' | 'REACTIVE', data: any) => {
    setModalProps((prev:any) => ({
      ...prev,
      visible: true,
      title: type === 'DELETE' ? t("Roles.deleteModalRolesTitle") : t("Roles.reactiveModalRolesTitle"),
      content: `${t("Roles.modalRolesContent1Label")} "${
        data?.name
      }" ${type === 'DELETE' ? t("Roles.deleteModalRolesContent2Label") : t("Roles.reactiveModalRolesContent2Label")}`,
      onConfirm: () => {
        type === 'DELETE' ? onDeleteAction(data!) : onReactiveAction(data!);
      },
    }));
  };

  const onClearFilters = () => {
    setAdministrationFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      type: { value: null, matchMode: FilterMatchMode.CONTAINS },
      name: { value: null, matchMode: FilterMatchMode.CONTAINS },
      roleCategory: { value: null, matchMode: FilterMatchMode.CONTAINS },
      description: { value: null, matchMode: FilterMatchMode.CONTAINS },
      status: { value: null, matchMode: FilterMatchMode.CONTAINS },
      createdAt: { value: null, matchMode: FilterMatchMode.CONTAINS },
      deletedAt: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(formBlankValues)
  };

  const onClearFiltersColumn = () => {
    setAdministrationFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      type: { value: null, matchMode: FilterMatchMode.CONTAINS },
      name: { value: null, matchMode: FilterMatchMode.CONTAINS },
      roleCategory: { value: null, matchMode: FilterMatchMode.CONTAINS },
      description: { value: null, matchMode: FilterMatchMode.CONTAINS },
      status: { value: null, matchMode: FilterMatchMode.CONTAINS },
      createdAt: { value: null, matchMode: FilterMatchMode.CONTAINS },
      deletedAt: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.resetField("type")
    form.resetField("name")
    form.resetField("roleCategory")
    form.resetField("description")
    form.resetField("status")
    form.resetField("createdAt")
    form.resetField("deletedAt")
  };

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: col.field === 'name'
            ? t(`Roles.rol${col.field}ColumnTitle`)
            : t(`Roles.${col.field}ColumnTitle`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  // ##### CAMBIAR LAS LABELS #####
  const filterTemplate = (options: any) => {
    return (
      <Controller
          name={options.field}
          control={control}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.target.value);
              options.filterCallback(e.target.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-2">
                  {t(`Roles.${options.field}Label`)}
                </label>
                <InputText
                  placeholder={t(`Roles.${options.field}FilterPlaceholder`)} 
                  autoComplete="off"
                  type={'text'}
                  className="mb-2"
                  {...field}
                />
              </>
            );
          }}
      />
    )
  }

  const filterClearTemplate = (options: any) => {
      return (
        <Button
          label={t("ValidationMessages.clearFilterLabel")}
          variant="p-button-text"
          onClick={onClearFiltersColumn}
        />
      )
  }
    
  const filterApplyTemplate = (options: any) => {
      return (
        <Button
          label={t("ValidationMessages.applyFilterLabel")}
          className="p-button"
          onClick={()=>{
            options.filterApplyCallback();
            setAppState((prev: any)=>({
              ...prev,
              pageFilter: getValues(),
            }));
          }}
        />
      )
  }

  const statusBodyTemplate = (rowData:any) => (
    <Tag
      value={rowData.status}
      severity={
        (['active', 'activo'].includes(rowData.status.toLowerCase()) && 'success') ||
        (['draft', 'borrador'].includes(rowData.status.toLowerCase()) && 'info') ||
        (['delete', 'eliminado'].includes(rowData.status.toLowerCase()) && 'danger') ||
        'warning'
      }
    />
  );

  const allFieldsBodyTemplate = (rowData:any, field: string) => {
    if (roleTypeId === 'client' ) {
      const isRolBase = rowData.roleTypeId.toLowerCase() === 'base';
      return (
        <div
          style={{
            boxShadow : (isRolBase && field !== 'actions')
              ? 'var(--backgroundColor) 0px 0px 0px 1800px'
              : 'none',
            background: (isRolBase && field !== 'actions')
              ? 'var(--backgroundColor)'
              : 'none',
          }}
        >
          {
            field === "status"
              ? statusBodyTemplate(rowData)
              : rowData[field]
          }
        </div>)
    }
    else {
      return field === "status"
        ? statusBodyTemplate(rowData)
        : rowData[field]
    }
  };

  return (
    <>
      {!isDeleteComponent &&
        <BoxFull className="mt-0 mx-0 px-0 pb-3" variant="tertiary">
          <div className="subheader-buttons">
            <SubheaderActions
              className="p-0 m-0"
              actions={subheaderActionsPrivilegeValidator([
                {
                  id: "create-role",
                  icon: "iu iu-icon-plus",
                  label: t("Roles.createBtnLabel"),
                  style: {
                    display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
                  },
                  onClick: () => navigate("/adminroles/newroles"),
                },
              ])}
            />
          </div>
        </BoxFull>
      }
      <BoxFull className={`dt-c mx-0 roles-management-boxfull ${isDeleteComponent && "mt-5"} mb-4`} variant="tertiary" style={{ backgroundColor: 'var(--cardBackgroundColorOne)' }}>
        {data ? (
          <DataTable
            header={
              <TableHeader
              title={t(`Roles.${isDeleteComponent ? 'deleted' : 'available'}TableTitle`)}
              columns={columns}
              selectedColumns={selectedColumns}
              toggleColumns={setSelectedColumns}
              filters={administrationFilters}
              setFilters={setAdministrationFilters}
              reload={reloadFn}
              onClearFiltersColumn={onClearFiltersColumn}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={<EmptyTemplate image={noResultsImage} text={t('Roles.emptyDataTableMessage')} />}
            rows={10}
            size="normal"
            filters={administrationFilters}
            sortMode={isDeleteComponent ? "single" : "multiple"}
            sortField={isDeleteComponent ? "deletedDate" : undefined}
            sortOrder={isDeleteComponent ? -1 : undefined}
            multiSortMeta={!isDeleteComponent ? [{field: "type", order: 1}, {field: "createdDate", order: -1}] : undefined}
            dataKey="role-id"
            value={
              data?.filter((roleItem: any) => {
                return isDeleteComponent
                  ? roleItem?.statusId === 'DELETED'
                  : roleItem?.statusId !== 'DELETED'
              })
              .map((roleItem: any) => ({
              ...roleItem,
              type: roleTypeTranslator(t, roleTypesObject, roleItem),
              roleCategory: !!roleItem?.isAdministrative ? t('Roles.adminRoleTitle') : t('Roles.applicationRoleTitle'),
              status: t(`Roles.${roleItem?.statusId}Label`),
              createdAt: !!roleItem?.createdAt ? formatStringToDate2(roleItem?.createdAt, true, t) : null,
              deletedAt: !!roleItem?.deletedAt ? formatStringToDate2(roleItem?.deletedAt, true, t) : '-',
              createdDate: roleItem?.createdAt,
              deletedDate: roleItem?.deletedAt,
              actions: !isDeleteComponent
              ? (
                <div className="role-actions-datatable">
                  <Actions
                    enableDelete={
                      roleItem?.roleTypeId.toLowerCase() !== 'base' ||
                        isMainTenant
                      }
                    enableEdit={false}
                    auxButtons={[
                      {
                        label: t("Roles.cloneBtnLabel"),
                        icon: "iu-icon-copy",
                        function: () => {
                          onAction("CLONE", roleItem);
                          window.scrollTo(0,0);
                        },
                        color: "var(--infoColor)",
                        order: "-1",
                        showButton: permissiondInfoByRole.CREATE
                      },
                      {
                        label: t("Roles.editBtnLabel"),
                        icon: "iu-icon-edit",
                        function: () => {
                          onAction("UPDATE", roleItem);
                          window.scrollTo(0,0);
                        },
                        color: !(roleItem?.roleTypeId.toLowerCase() !== 'base' ||
                          isMainTenant)
                          ? "var(--warningColorTint60)" : "var(--warningColor)",
                        order: "-2",
                        disabled: !(roleItem?.roleTypeId.toLowerCase() !== 'base' ||
                          isMainTenant),
                        showButton: permissiondInfoByRole.UPDATE
                      },
                      {
                        label: t("Roles.detailBtnLabel"),
                        icon: "iu-icon-detail",
                        function: () => {
                          onAction("DETAIL", roleItem);
                          window.scrollTo(0,0);
                        },
                        color: "var(--successColor)",
                        order: "-3",
                      }
                    ]}
                    onDelete={() => handleModalProps('DELETE', roleItem)}
                    visibleObject={{ ...permissiondInfoByRole }}
                  />
                </div>
                )
              : (
                  <Button
                    icon="iu"
                    className="p-button-rounded p-button-success p-button-outlined act-btn"
                    aria-label="Detail"
                    tooltip={t("TableActions.detailLabel")}
                    tooltipOptions={{ position: "bottom" }}
                    variant="p-button"
                    onClick={() => {
                      onAction("DETAIL", roleItem);
                      window.scrollTo(0,0);
                    }}
                    style={{
                      color: "var(--successColor)",
                    }}
                  >
                    <i className="iu iu-icon-detail act-btn detail" />
                  </Button>
                ),
              }))}
            scrollable
            resizableColumns
            globalFilterFields={[
              "type",
              "name",
              "roleCategory",
              "description",
              "status",
              "createdAt",
              "deletedAt",
            ]}
            filterDisplay="menu"
            rowsPerPageOptions={[10,20,50]}
            >
            {selectedColumns.map((col: any) => {
              return (
                <Column
                  key={col.field}
                  className="roles-column"
                  field={col.field}
                  header={col.header}
                  sortable={!(col.field === "actions")}
                  align={col.field === "actions"  ? "center" : "left"}
                  headerStyle={
                    col.field === "actions" 
                    ? {
                        justifyContent: "center",
                        maxWidth: "195px",
                        width: "195px",
                        minWidth: "195px",                      }
                    : {
                      maxWidth: "100%",
                      width: "100%",
                    }
                  }
                  style={{
                    boxShadow:
                    col.field === "actions"
                    ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                    : undefined,
                    maxWidth: col.field === "actions" ? "195px" : "100%",
                    whiteSpace: "normal",
                    width: col.field === "actions" ? "195px" : "155px",
                    minWidth: col.field === "actions" ? "195px" : '160px',
                  }}
                  frozen={col.field === "actions" ? true : false}
                  alignFrozen={col.field === "actions" ? "right" : undefined}
                  filterField={col.field}
                  showFilterMatchModes={false}
                  filterMenuStyle={{ width: "23rem" }}
                  filter={col.field === "actions" ? false : true}
                  filterElement={filterTemplate}
                  filterApply={filterApplyTemplate}
                  filterClear={filterClearTemplate}
                  body={(rowData) => allFieldsBodyTemplate(rowData, col.field)}
                />
                );
              })}
          </DataTable>
        ) : (
          <FormSkeleton length={8} />
          )}
      </BoxFull>
      <CustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
